import React, { memo, useCallback, useMemo } from 'react';
import { Row } from '@tanstack/react-table';

import CollapsibleActionsMenu from '@components/common/CollapsibleActionsMenu/CollapsibleActionsMenu';
import { IAssetVisibilityData } from '@components/routes/AdminPanel/AssetsList/AssetsList';

import { useQueryIsVisibleShowHideAssets } from '@hooks/assets/useQueryIsVisibleShowHide';

import { assets as assetsSelector } from '@store/selectors';
import { useAppSelector } from '@store/store';

import { TAsset } from '@interfaces/assets/asset';

import { EAssetRentStatuses } from '@enums/asset';

import { TAssetVisibilityActions } from './constants';

import {
	getActionsAttributes,
	getAssetIdsForAction,
	getAssetVisibilityActionOptions,
} from './utils';

interface IAssetVisibilityActionsMenuParams {
	rows: Row<TAsset>[];
	selectedIds: string[];
	isVisibleMenu: boolean;
	handleVisibilityAction: ({ dto, isHide }: IAssetVisibilityData) => void;
	isDirtyFilters?: boolean;
}

const AssetVisibilityActionsMenu = ({
	rows,
	selectedIds,
	isVisibleMenu,
	handleVisibilityAction,
	isDirtyFilters,
}: IAssetVisibilityActionsMenuParams) => {
	const {
		assetSiteAvailabilityStatus,
		searchValue,
		assetSubTypeId,
		assetTypeId,
		backhaulAccessibilityStatus,
		contactPersonId,
		createdAt,
		isAvailable,
		lastVerificationDate,
		powerSupplyAvailabilityStatus,
		updatedAt,
		rentStatus,
	} = useAppSelector(assetsSelector);

	const { visibleByFilter, visibleOnAll } = useQueryIsVisibleShowHideAssets({
		assetSiteAvailabilityStatus,
		assetSubTypeId,
		assetTypeId,
		backhaulAccessibilityStatus,
		contactPersonId,
		createdAt,
		isAvailable,
		lastVerificationDate,
		powerSupplyAvailabilityStatus,
		rentStatus,
		searchValue,
		updatedAt,
	});

	const pageRows = useMemo(() => {
		return rows
			.filter(row => row.original.rentStatus?.name !== EAssetRentStatuses.active)
			.map((row) => row.original);
	}, [rows]);
	const selectedRows = useMemo(() => pageRows.filter((row) => selectedIds.includes(row.id)), [pageRows, selectedIds]);

	const options = useMemo(() => getAssetVisibilityActionOptions({
		rows: pageRows,
		selectedRows,
		visibleByFilter,
		visibleOnAll,
		isDirtyFilters,
	}), [pageRows, selectedRows, visibleByFilter, visibleOnAll, isDirtyFilters]);

	const onClick = useCallback((actionId: TAssetVisibilityActions) => {
		const ids = getAssetIdsForAction(actionId, pageRows, selectedRows);

		const {
			isAllAssets,
			isAssetIds,
			isFilter,
			isHide,
		} = getActionsAttributes(actionId);

		const rentStatus = EAssetRentStatuses.inactive;

		const filter = {
			assetSiteAvailabilityStatus,
			assetSubTypeId,
			assetTypeId,
			backhaulAccessibilityStatus,
			contactPersonId,
			createdAt,
			isAvailable,
			lastVerificationDate,
			powerSupplyAvailabilityStatus,
			rentStatus,
			searchValue,
			updatedAt,
		};

		const dto = {
			assetIds: isAssetIds ? ids : [],
			isAll: isAllAssets || isFilter,
			filter: isFilter ? filter : { rentStatus },
		};

		handleVisibilityAction({
			isHide,
			dto,
		});
	}, [
		searchValue,
		assetSiteAvailabilityStatus,
		assetSubTypeId,
		assetTypeId,
		backhaulAccessibilityStatus,
		contactPersonId,
		createdAt,
		isAvailable,
		lastVerificationDate,
		pageRows,
		powerSupplyAvailabilityStatus,
		selectedRows,
		updatedAt,
		handleVisibilityAction,
	]);

	return (
		<CollapsibleActionsMenu
			actions={options}
			isVisibleMenu={isVisibleMenu}
			onClick={onClick}
		/>
	);
};

export default memo(AssetVisibilityActionsMenu);
